<template>
  <main id="create-quote-page" class="page-container">
    <button type="button" class="back-btn" @click="goBack">
      <i class="fa-solid fa-arrow-left"></i> Indietro
    </button>
    <!-- [sc-49299] nuovo articolo dal preventivo -->
    <CreaArticoloModal
      v-model:show="showCreaArticolo"
      :nome-iniziale="nomeNuovoArticolo"
      @creato="onArticoloCreato"
    />

    <VueFinalModal
      v-model="showDuplicateModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content dup-modal__content"
    >
      <template #default>
        <button class="modal__close icons-button" @click="closeDuplicateModal">
          <i class="fa-solid fa-xmark"></i>
        </button>

        <h3 class="modal__title">Preventivo già presente</h3>

        <p class="dup-modal__intro">
          Esiste già un preventivo per questo cliente allo stesso indirizzo in
          corso...
          <span
            v-if="lastDuplicateQuoteInfo?.address"
            class="dup-modal__address"
          >
            ({{ lastDuplicateQuoteInfo.address }}) </span
          >.
        </p>

        <!-- loading -->
        <div v-if="dupLoading" class="dup-modal__skeleton">
          Caricamento in corso…
        </div>
        <!-- error -->
        <div v-else-if="dupError" class="dup-modal__error">{{ dupError }}</div>

        <!-- dati -->
        <div v-else-if="dupQuote" class="dup-quote">
          <div class="dup-quote__grid">
            <div>
              <div class="dup-quote__label">ID</div>
              <div class="dup-quote__value">#{{ dupQuote.id }}</div>
            </div>
            <div>
              <div class="dup-quote__label">Data</div>
              <div class="dup-quote__value">
                {{ formatToLocalDate(dupQuote.date) }}
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Cliente</div>
              <div class="dup-quote__value">
                {{ dupQuote.clientName || "—" }}
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Pagamento</div>
              <div class="dup-quote__value">{{ dupQuote.payment_method }}</div>
            </div>
            <div>
              <div class="dup-quote__label">Indirizzo</div>
              <div class="dup-quote__value">
                <a
                  v-if="dupQuote.location?.link"
                  :href="dupQuote.location.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link"
                >
                  {{ dupQuote.location?.address || "Vedi mappa" }}
                </a>
                <span v-else>{{ dupQuote.location?.address || "—" }}</span>
              </div>
            </div>
            <div>
              <div class="dup-quote__label">Totale</div>
              <div class="dup-quote__value">
                {{ money(dupQuote.totalPrice) }}
              </div>
            </div>
          </div>

          <div class="dup-quote__items">
            <div class="dup-quote__items-head">
              <strong>Riepilogo articoli</strong>
              <span class="muted">({{ dupQuote.items?.length || 0 }})</span>
            </div>
            <ul class="dup-quote__items-list">
              <li
                v-for="(it, idx) in dupQuote.items || []"
                :key="idx"
                class="dup-item"
              >
                <div class="dup-item__name">
                  {{ it.itemName || it.brand || "Articolo" }}
                </div>
                <div class="dup-item__meta">
                  <span v-if="it.category" class="chip">{{ it.category }}</span>
                  <span v-if="it.boxQuantity"
                    >scatole: {{ it.boxQuantity }}</span
                  >
                  <span v-if="it.pieces">pezzi: {{ it.pieces }}</span>
                  <span v-if="it.iva !== undefined">IVA: {{ it.iva }}%</span>
                  <span v-if="it.price !== undefined"
                    >€ {{ number(it.price) }}</span
                  >
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="dup-modal__footer">
          <button
            v-if="dupQuote && !dupLoading && dupDocuments.length"
            class="btn-primary"
            @click="showDupDocs = true"
          >
            Apri file
          </button>

          <button class="btn-primary" @click="onChangeAddress">
            Usa indirizzo diverso
          </button>

          <button
            class="btn-ghost"
            :disabled="!lastDuplicateQuoteInfo?.quoteId"
            @click="onDuplicateWithEdit"
          >
            Duplica modificando indirizzo
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- Modale documenti PDF del preventivo duplicato -->
    <VueFinalModal
      v-model="showDupDocs"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="documents-modal-content"
    >
      <template #default>
        <button class="modal__close icons-button" @click="showDupDocs = false">
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Preventivo #{{ dupQuote?.id }}</h3>

        <div class="documents-grid">
          <div v-for="doc in dupDocuments" :key="doc.id" class="document-card">
            <a href="#" class="card-link" @click.prevent="openDoc(doc)">
              <div class="card-icon-pdf">
                <i class="fa-solid fa-file-pdf"></i>
              </div>
              <div class="card-body">
                <div class="card-title">{{ formatDocName(doc.name) }}</div>
                <div class="card-subtitle">
                  Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                </div>
              </div>
            </a>
          </div>
          <div v-if="dupDocuments.length === 0" class="no-result">
            Nessun documento trovato
          </div>
        </div>
      </template>
    </VueFinalModal>

    <div class="create-quote-wrapper">
      <div class="create-quote-main-col" ref="mainCol">
        <!-- Form principale -->
        <form
          class="create-quote-form"
          @submit.prevent="onUpdateQuoteAndGeneratePdf"
        >
          <!-- RIGA 1: Seleziona Cliente, data di inizio noleggio, data di fine noleggio e Operatore, Aggiungi Zona e categoria di articolo -->
          <div class="create-quote-row row-1 form-stack">
            <!-- Seleziona Cliente -->
            <div class="field" id="client-select">
              <label>Cliente *</label>

              <SearchableCombo
                v-model="clientIdModel"
                v-model:text="searchQueryClient"
                :items="filteredClients"
                :labelKey="getClientLabel"
                :valueKey="(c) => String(c?.id)"
                placeholder="Cerca cliente…"
                :loading="false"
                @search="
                  (q) => {
                    searchQueryClient = q || '';
                  }
                "
                @select="
                  (c) => {
                    formData.clientId = c?.id ?? '';
                  }
                "
              />
            </div>

            <!-- Data Preventivo -->
            <div class="field">
              <label for="date">Data</label>
              <FlatPickr
                id="date"
                v-model="formData.date"
                :config="dateConfig"
                placeholder="gg/mm/aaaa"
              />
            </div>
          </div>
          <div class="create-quote-row row-2 form-stack">
            <!-- Località -->
            <div class="field">
              <small v-if="addressDirty && !isAddressValid" class="help-error">
                Scrivi l'indirizzo di consegna.
              </small>
              <label for="indirizzo-consegna">Indirizzo di consegna *</label>
              <!-- [sc-48860] propone residenza e cantiere del cliente -->
              <IndirizzoConsegna
                v-model="formData.location.address"
                :cliente="clienteSelezionato"
                @input="onAddressTyping"
                @blur="addressDirty = true"
              />
            </div>

            <!-- modalità di pagamento -->
            <div class="field">
              <label for="payment-method">Modalità di pagamento *</label>
              <StatusSelect
                v-model="formData.payment_method"
                :options="paymentOptions"
                placeholder="Seleziona metodo..."
                placeholderIcon="fa-wallet"
              />
            </div>
          </div>
          <hr />

          <div class="create-quote-row row-3 form-stack">
            <!-- scegli Articolo -->
            <div class="field" id="item-select">
              <label>Articolo *</label>

              <SearchableCombo
                ref="itemCombo"
                v-model="newItem.itemId"
                v-model:text="searchQueryItem"
                :items="filteredItems"
                :labelKey="itemSearchLabel"
                :valueKey="(it) => it?.id"
                placeholder="Cerca articolo…"
                :disabled="false"
                :loading="itemsLoading"
                :footerText="conteggioArticoli"
                @search="(q) => debouncedFetchItems(q || '')"
                @open="(q) => debouncedFetchItems(q || '')"
                @select="selectItem"
                @cleared="clearItemSelection"
                @load-more="caricaAltriArticoli"
              >
                <template #option="{ option }">
                  <span
                    class="quote-item-option__label"
                    :class="{ 'is-out': isOrderableOutOfStock(option) }"
                  >
                    <span v-if="isOrderableOutOfStock(option)">! </span
                    >{{ itemSearchLabel(option) }}
                  </span>
                  <!-- [sc-48920] codice · brand · serie: perché è uscito -->
                  <span
                    v-if="dettaglioVoceArticolo(option)"
                    class="quote-item-option__sub"
                    >{{ dettaglioVoceArticolo(option) }}</span
                  >
                </template>
                <!-- [sc-49389] ricerca senza risultati: si crea da qui -->
                <template #empty="{ close }">
                  <span class="crea-articolo-riga">
                    Nessun risultato
                    <button
                      type="button"
                      class="crea-articolo-link"
                      @click="creaArticoloDaRicerca(close)"
                      @touchend.prevent="creaArticoloDaRicerca(close)"
                    >
                      <i class="fa-solid fa-circle-plus" aria-hidden="true"></i>
                      <span>Crea articolo</span>
                    </button>
                  </span>
                </template>
              </SearchableCombo>
              <!-- [sc-49299] articolo non a listino: si crea da qui -->
              <p class="crea-articolo-riga">
                Articolo non trovato?
                <button
                  type="button"
                  class="crea-articolo-link"
                  @click="showCreaArticolo = true"
                >
                  <i class="fa-solid fa-circle-plus" aria-hidden="true"></i>
                  <span>Crea articolo</span>
                </button>
              </p>
            </div>

            <!-- Scegli quantità -->
            <div class="field">
              <label for="quantity">{{ quantityLabel }} *</label>
              <input
                id="quantity"
                step="any"
                type="number"
                v-model="quantityInput"
                placeholder="Inserisci"
              />
            </div>

            <!-- quantità al Mq / ML => readonly -->
            <div class="field" v-if="showMqField">
              <label for="mq-per-box">{{ mqFieldLabel }}</label>
              <input
                :value="mqPerQuantity"
                type="text"
                placeholder="Seleziona la quantità di scatole per visualizzare i mq"
                readonly
                class="readonly-field"
              />
            </div>
          </div>

          <div class="create-quote-row row-4 form-stack">
            <!-- Prezzi: listino fisso, scontato correggibile (IVA esclusa) -->
            <div class="field">
              <label for="list-price">Prezzo listino — IVA esclusa</label>
              <input
                id="list-price"
                type="text"
                :value="
                  newItem.listPrice != null ? `€ ${eur(newItem.listPrice)}` : ''
                "
                placeholder="Seleziona l'articolo per visualizzare il prezzo corrispondente"
                readonly
                class="readonly-field"
              />
            </div>
            <div class="field">
              <label for="discounted-price">Prezzo scontato</label>
              <input
                id="discounted-price"
                type="number"
                v-model="newItem.discountedPrice"
                placeholder="Prezzo netto scontato (opzionale)"
                step="0.01"
                min="0"
                :disabled="!newItem.itemId"
                :class="{ invalid: erroreScontatoRiga }"
              />
              <small v-if="erroreScontatoRiga" class="help-error">{{
                erroreScontatoRiga
              }}</small>
            </div>
            <div class="field">
              <label for="vat-rate">IVA</label>
              <StatusSelect
                v-model="newItem.iva"
                :options="ivaOptions"
                placeholderIcon="fa-percent"
              />
            </div>

            <!-- Anteprima della riga prima di aggiungerla -->
            <div
              class="item-preview"
              :class="{ 'is-invalid': !!erroreScontatoRiga }"
              aria-live="polite"
            >
              <div>
                Imponibile:
                <b>{{ importoAnteprima(anteprimaNuovaRiga.imponibile) }}</b>
              </div>
              <div>
                Aliquota: <b>{{ anteprimaNuovaRiga.aliquota }}%</b>
              </div>
              <div>
                Imposta:
                <b>{{ importoAnteprima(anteprimaNuovaRiga.imposta) }}</b>
              </div>
              <div class="item-preview__total">
                Totale: {{ importoAnteprima(anteprimaNuovaRiga.totale) }}
              </div>
            </div>
          </div>

          <!-- RIGA 5: bottone "+ aggiungi articolo" + "Mostra articoli" -->
          <div class="create-quote-row row-5">
            <!-- Bottone per aggiungere un ulteriore alla lista -->
            <div class="field">
              <button
                type="button"
                class="add-item-btn"
                @click="addOrUpdateItem"
              >
                <i class="fa-solid fa-plus" aria-hidden="true"></i>
                <span>Aggiungi articolo</span>
              </button>
            </div>

            <!-- Apri pannello articoli (visibile solo quando chiuso) -->
            <button
              v-if="!panelVisible"
              type="button"
              @click="toggleItemsList"
              class="panel-open-btn"
              title="Mostra articoli aggiunti"
            >
              <i class="fa-solid fa-list"></i>
              Mostra articoli
            </button>
          </div>
        </form>

        <hr />

        <!-- [sc-49299] spese aggiuntive, senza IVA -->
        <div class="create-quote-row spese-row">
          <SpeseAggiuntive v-model="spese" />
        </div>

        <hr />

        <!-- RIGA 6: note + cambio lingua -->
        <div class="create-quote-row row-5 notes-row">
          <!-- Richieste tecniche -->
          <div class="field">
            <label for="technical-requests">Note</label>
            <textarea
              id="technical-requests"
              v-model.trim="formData.technical_queries"
              placeholder="Inserisci note"
              rows="3"
            ></textarea>
          </div>
          <!-- Esempio di dropdown per il cambio lingua -->
          <!-- <div class="field">
            <label for="lang-select">Lingua PDF:</label>
            <select v-model="$i18n.locale" id="lang-select" class="pdf-lang-select">
              <option value="it">Italiano</option>
              <option value="en">English</option>
            </select>
          </div> -->
        </div>
      </div>
      <!-- /.create-quote-main-col -->

      <!-- Backdrop mobile (clic per chiudere) -->
      <div
        v-if="panelVisible"
        class="panel-backdrop"
        @click="toggleItemsList"
      ></div>

      <!-- Colonna pannello + bottoni salva -->
      <div class="panel-col" v-show="panelVisible">
        <aside class="items-panel" ref="itemsPanel">
          <div class="items-panel__title-bar">
            <h3 class="items-panel__title">Articoli aggiunti</h3>
            <button
              type="button"
              @click="toggleItemsList"
              class="items-panel__close-btn"
              title="Chiudi pannello"
              aria-label="Chiudi pannello articoli"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div class="items-panel__table-wrap">
            <table class="items-panel__table">
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Articolo</th>
                  <th>Categoria</th>
                  <th>Qtà</th>
                  <th>Misura</th>
                  <th>Prezzo ivato</th>
                  <th>IVA</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in formData.items" :key="index">
                  <td class="item-code-cell">
                    {{
                      getItemFromStore(item.itemId, categoryHintForRow(item))
                        .itemCode ?? "—"
                    }}
                  </td>
                  <td>
                    <div class="item-title">
                      {{
                        displayForId(item.itemId, categoryHintForRow(item))
                          .title
                      }}
                    </div>
                    <div
                      v-if="
                        displayForId(item.itemId, categoryHintForRow(item)).meta
                      "
                      class="item-meta"
                    >
                      {{
                        displayForId(item.itemId, categoryHintForRow(item)).meta
                      }}
                    </div>
                  </td>
                  <td>
                    {{
                      getItemFromStore(item.itemId, categoryHintForRow(item))
                        .category
                    }}
                  </td>
                  <td>
                    <input
                      type="number"
                      class="panel-qty-input"
                      :value="qtyCell(item)"
                      min="0"
                      step="1"
                      @change="updateRowQty(item, +$event.target.value)"
                    />
                    <template
                      v-if="
                        isOrderableOutOfStock(
                          getItemFromStore(
                            item.itemId,
                            categoryHintForRow(item)
                          )
                        )
                      "
                    >
                      <span
                        v-if="isOrderQueuedRow(item)"
                        class="item-order-queued-badge"
                        >Ordine in coda</span
                      >
                      <span v-else class="item-order-queued-badge"
                        >Esaurito</span
                      >
                      <button
                        type="button"
                        class="quote-order-btn"
                        :class="{ 'is-cancel': isOrderQueuedRow(item) }"
                        @click.stop="
                          isOrderQueuedRow(item)
                            ? unqueueOrderFromQuoteItem(
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                )
                              )
                            : queueOrderFromQuoteItem(
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                )
                              )
                        "
                      >
                        {{ isOrderQueuedRow(item) ? "Annulla" : "Ordina" }}
                      </button>
                    </template>
                    <template v-else-if="isQtyExceedingStock(item)">
                      <span
                        v-if="isOrderQueuedRow(item)"
                        class="item-order-queued-badge"
                        >Ordine in coda</span
                      >
                      <span
                        v-else
                        class="item-order-queued-badge item-not-available-badge"
                        >Non disponibile</span
                      >
                      <button
                        type="button"
                        class="quote-order-btn"
                        :class="{ 'is-cancel': isOrderQueuedRow(item) }"
                        @click.stop="
                          isOrderQueuedRow(item)
                            ? unqueueOrderFromQuoteItem(
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                )
                              )
                            : queueOrderFromQuoteItem(
                                getItemFromStore(
                                  item.itemId,
                                  categoryHintForRow(item)
                                )
                              )
                        "
                      >
                        {{ isOrderQueuedRow(item) ? "Annulla" : "Ordina" }}
                      </button>
                    </template>
                  </td>
                  <td>{{ mqCell(item) }}</td>
                  <td>{{ formatPrice(item.price) }} {{ unitForRow(item) }}</td>
                  <td>{{ formatIva(item.iva) }}</td>
                  <td>
                    <button
                      class="icons-button"
                      id="remove-item-button"
                      @click="removeItem(index)"
                      title="Rimuovi articolo dalla lista"
                      aria-label="Rimuovi articolo dalla lista"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="formData.items.length === 0">
                  <td colspan="8" class="no-result">
                    Nessun articolo presente
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="items-panel__footer">
            {{ formData.items.length }}
            {{ formData.items.length === 1 ? "articolo" : "articoli" }}
            <button
              class="icons-button"
              type="button"
              title="Genera DDT dalla lista materiale"
              :disabled="formData.items.length === 0"
              @click="showDdtModal = true"
              style="margin-left: 8px"
            >
              <i class="fa-solid fa-truck"></i>
            </button>
          </div>
        </aside>

        <div class="save-button-container">
          <button
            type="submit"
            id="save-quote"
            class="primary-btn"
            @click="onUpdateQuoteAndGeneratePdf"
            :disabled="!canSave"
            :aria-disabled="!canSave ? 'true' : 'false'"
            :title="
              erroreSpese
                ? `Spese aggiuntive — ${erroreSpese}`
                : !canSave
                ? 'Compila i campi obbligatori (*) e modifica almeno un campo per vedere l’anteprima'
                : 'Vedi il PDF, scegli cosa stampare e aggiorna il preventivo'
            "
          >
            Anteprima preventivo
          </button>
        </div>
      </div>
      <!-- /.panel-col -->
    </div>
    <!-- /.create-quote-wrapper -->
    <DdtModal
      :show="showDdtModal"
      :items="ddtItems"
      :quote-id="Number(quoteId) || null"
      :client-id="formData.clientId || null"
      :client-name="getClientName(formData.clientId)"
      @update:show="showDdtModal = $event"
    />

    <VueFinalModal
      v-model="showBackModal"
      :clickToClose="true"
      :escToClose="true"
      classes="documents-modal"
      content-class="confirm-modal-content"
    >
      <template #default>
        <button
          class="modal__close icons-button"
          @click="showBackModal = false"
          title="Chiudi"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="modal__title">Tornare indietro?</h3>
        <p class="modal__subtitle">Le modifiche non salvate andranno perse.</p>
        <div class="modal-actions">
          <button class="secondary-btn" @click="showBackModal = false">
            Annulla
          </button>
          <button class="primary-btn confirm-btn" @click="confirmGoBack">
            Conferma
          </button>
        </div>
      </template>
    </VueFinalModal>

    <!-- [sc-49299] «Anteprima preventivo»: si salva solo da qui -->
    <StampaPreventivoModal
      :show="stampa.show"
      :dati="stampa.dati"
      :salvando="stampa.salvando"
      etichetta-scarica="Aggiorna e scarica PDF"
      @scarica="onScaricaStampa"
      @chiudi="onChiudiStampa"
    />
  </main>
</template>

<script>
// import crypto from "crypto";
import axiosInstance from "@/axiosInstance";
import { eur } from "@/utilities/pdfRiepilogo";
import debounce from "lodash/debounce";
// import { nextTick } from "process";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import { formatToLocalDate, toDateLocal } from "../utilities/formatting";
import SearchableCombo from "./SearchableCombo.vue";
import CreaArticoloModal from "@/components/CreaArticoloModal.vue";
import SpeseAggiuntive from "@/components/SpeseAggiuntive.vue";
import {
  primoErroreSpese,
  speseDaMandare,
  speseDaPreventivo,
} from "@/utilities/spese";
import ricercaArticoliPreventivo from "@/mixins/ricercaArticoliPreventivo";
import apriDocumento from "@/mixins/apriDocumento";
import stampaPreventivo from "@/mixins/stampaPreventivo";
import StampaPreventivoModal from "@/components/StampaPreventivoModal.vue";
import { clientePerPdf, datiPdf } from "@/utilities/pdfPreventivo";
import {
  importiDaPercentuali,
  prezzoIvatoRiga,
  totaleArticoli,
} from "@/utilities/anteprimaPreventivo";
import DdtModal from "@/components/DdtModal.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import { clientiPerRicerca } from "@/utilities/cliente";
import IndirizzoConsegna from "@/components/IndirizzoConsegna.vue";
import { dettaglioVoceArticolo } from "@/utilities/filtriListino";
import {
  erroreScontato,
  numeroPrezzo,
  prezzoBase,
  prezzoIvato,
  prezziRigaSalvata,
  anteprimaRiga,
} from "@/utilities/prezzoArticolo";
import FlatPickr from "vue-flatpickr-component";
import { Italian } from "flatpickr/dist/l10n/it.js";
import { linkMaps, indirizzoValido } from "@/utilities/indirizzo";

// Chiavi cache namespaced
const asKey = (id) => String(id ?? ""); // normalizza sempre a stringa
const KEY = {
  STOCK: (id) => `STOCK:${asKey(id)}`,
};

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  linear: ["profili", "sistemi di scarico"],
  furniture: ["mobili", "porte e infissi"],
};

function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

export default {
  name: "EditQuotePage",
  // [sc-48920] menu articolo a pagine: filteredItems, itemsLoading, fetchItems
  mixins: [ricercaArticoliPreventivo, apriDocumento, stampaPreventivo],
  components: {
    SearchableCombo,
    StampaPreventivoModal,
    CreaArticoloModal,
    SpeseAggiuntive,
    DdtModal,
    StatusSelect,
    FlatPickr,
    IndirizzoConsegna,
  },
  beforeRouteLeave(to) {
    if (this.navigationConfirmed || to.name === "login") {
      this.navigationConfirmed = false;
      return true;
    }
    this.pendingNavigation = to;
    this.showBackModal = true;
    return false;
  },
  props: ["quoteId"],
  created() {
    this.$store
      .dispatch("fetchClients", {
        limit: 10000,
      })
      .catch((err) => {
        // evita Uncaught (in promise)
        console.error("Impossibile caricare i clienti:", err.message);
        // puoi mostrare un toast o impostare un flag di errore
      });
    // this.$store.dispatch("fetchOperators");
    this.$store.dispatch("fetchItems", { limit: 10000 }).catch((err) => {
      // evita Uncaught (in promise)
      console.error("Impossibile caricare gli articoli:", err.message);
      // puoi mostrare un toast o impostare un flag di errore
    });
    this.debouncedFetchItems = debounce(this.fetchItems, 200);
  },
  computed: {
    // Anteprima della riga in inserimento: la quantità fatturata è in mq o ml
    // per le categorie a scatola, come nel totale del preventivo
    anteprimaNuovaRiga() {
      // Scontato non valido: niente cifre, solo l'errore sotto il campo
      if (this.erroreScontatoRiga) {
        return {
          imponibile: null,
          aliquota: this.newItem.iva,
          imposta: null,
          totale: null,
        };
      }
      const s = this.selectedItem || {};
      const g = getCategoryGroup(s.category);
      const scatole = Number(this.newItem.boxQuantity) || 0;
      const quantita =
        g === "area"
          ? scatole * (Number(s.sqmPerBox) || 1)
          : g === "linear"
          ? scatole * (Number(s.mlPerBox) || 1)
          : Number(this.newItem.pieces) || 0;
      return anteprimaRiga({
        listPrice: this.newItem.itemId ? this.newItem.listPrice : null,
        discountedPrice: this.newItem.itemId
          ? this.newItem.discountedPrice
          : null,
        iva: this.newItem.iva,
        quantita,
      });
    },
    // Lo scontato della riga in inserimento deve restare sotto il listino
    erroreScontatoRiga() {
      if (!this.newItem.itemId) return null;
      return erroreScontato(
        this.newItem.listPrice,
        this.newItem.discountedPrice
      );
    },
    canSave() {
      // vuoi anche impedire il salvataggio senza articoli
      const hasItems = (this.formData.items || []).length > 0;
      const dirty = this.computeSignature() !== this.originalSig;
      // [sc-49299] obbligatori come nel nuovo preventivo
      const obbligatori =
        !!this.formData.clientId &&
        !!this.formData.payment_method &&
        this.isAddressValid;
      return (
        hasItems &&
        obbligatori &&
        dirty &&
        !this.dupLoading &&
        !this.erroreSpese
      );
    },
    // [sc-49299] spesa spuntata senza importo valido: salvataggio spento
    erroreSpese() {
      return primoErroreSpese(this.spese);
    },
    isAddressValid() {
      // [sc-48762] l'indirizzo si scrive a mano: basta che sia compilato
      return indirizzoValido(this.formData.location?.address);
    },

    // selectedItem() {
    //   /* Se è stato scelto un articolo di magazzino */
    //   if (this.newItem.itemId) {
    //     return this.getItemFromStore(this.newItem.itemId); // ← già fa cache
    //   }
    //   /* Se è stato scelto un articolo FUORI magazzino */
    //   if (this.newItem.outstockId) {
    //     return (
    //       this.outstocks.find((o) => o.id === this.newItem.outstockId) || {}
    //     );
    //   }
    //   /* Nessuna selezione */
    //   return {};
    // },

    selectedItem() {
      if (this.newItem.itemId) {
        return this.getItemFromStore(this.newItem.itemId, "STOCK");
      }
      return {};
    },

    selectedItemGroup() {
      return getCategoryGroup(this.selectedItem?.category);
    },
    mqFieldLabel() {
      if (this.selectedItemGroup === "linear") return "Quantità ML";
      return "Quantità Mq";
    },
    quantityLabel() {
      if (this.selectedItemGroup === "area") return "Quantità scatole";
      if (this.selectedItemGroup === "linear") return "Quantità confezioni";
      if (
        this.selectedItem.category === "Fuori Magazzino" &&
        this.selectedItem.subCategory === "piastrelle"
      )
        return "Quantità (mq)";
      if (this.selectedItem.category === "Fuori Magazzino") return "Quantità";
      return "Quantità pezzi";
    },

    // quantityInput: {
    //   get() {
    //     return this.selectedItem.category === "piastrelle"
    //       ? this.newItem.boxQuantity // lavoro in scatole
    //       : this.newItem.pieces; // lavoro in pezzi
    //   },
    //   set(v) {
    //     const n = +v || 0;
    //     if (this.selectedItem.category === "piastrelle") {
    //       // console.log("selectedItem in quantityinput", this.selectedItem);
    //       this.newItem.boxQuantity = n; // scatole
    //       const ppb = this.selectedItem.piecesPerBox || 1;
    //       this.newItem.pieces = n * ppb; // pezzi calcolati
    //       // this.newItem.unitPrice;
    //     } else {
    //       this.newItem.pieces = n; // sanitari
    //     }

    //     // ricalcolo per FUORI MAGAZZINO: totale = unit × qty
    //     if (this.selectedItem.category === "Fuori Magazzino") {
    //       const isTiles = this.selectedItem.subCategory === "piastrelle";
    //       const base = isTiles
    //         ? Number(this.selectedItem.sqmPrice) || 0
    //         : Number(this.selectedItem.price) || 0;

    //       const unit = Number(this.adjustedPrice(base, this.newItem.iva));
    //       this.newItem.unitPrice = unit;

    //       const qty = this.newItem.pieces || 0; // anche per mq
    //       this.newItem.price = (unit * qty).toFixed(2); // mostra il totale nel campo
    //     }
    //   },
    // },

    quantityInput: {
      get() {
        const g = getCategoryGroup(this.selectedItem?.category);
        if (g === "area" || g === "linear") return this.newItem.boxQuantity;
        return this.newItem.pieces;
      },
      set(v) {
        const n = +v || 0;
        const g = getCategoryGroup(this.selectedItem?.category);

        if (g === "area") {
          this.newItem.boxQuantity = n;
          const ppb = this.selectedItem.piecesPerBox || 1;
          this.newItem.pieces = n * ppb;
        } else if (g === "linear") {
          this.newItem.boxQuantity = n;
          this.newItem.pieces = 0;
        } else {
          this.newItem.pieces = n;
        }

        if (this.selectedItem.category === "Fuori Magazzino") {
          const isTiles = this.selectedItem.subCategory === "piastrelle";
          const base = isTiles
            ? Number(this.selectedItem.sqmPrice) || 0
            : Number(this.selectedItem.price ?? this.newItem.unitPrice) || 0;

          const unit = Number(this.adjustedPrice(base, this.newItem.iva));
          this.newItem.unitPrice = unit;
          // this.newItem.pieces = n;

          const qty = this.newItem.pieces || 0;
          this.newItem.price = (unit * qty).toFixed(2);
        }
      },
    },

    /* Ponte per v-model mqPerQuantity */
    mqPerQuantity() {
      const s = this.selectedItem || {};
      const boxes = Number(this.newItem.boxQuantity) || 0;
      const g = getCategoryGroup(s.category);
      if (g === "area") {
        const sqmPerBox = Number(s.sqmPerBox) || 0;
        const total = boxes * sqmPerBox;
        return total ? total.toFixed(2) : "";
      }
      if (g === "linear") {
        const mlPerBox = Number(s.mlPerBox) || 0;
        const total = boxes * mlPerBox;
        return total ? total.toFixed(2) : "";
      }
      return "";
    },

    showMqField() {
      const s = this.selectedItem || {};
      if (!s || !s.category) return false;
      const g = getCategoryGroup(s.category);
      return g === "area" || g === "linear";
    },

    clients() {
      return this.$store.getters["clients/list"] ?? [];
    },
    // [sc-48860] cliente del preventivo, per proporre i suoi indirizzi di consegna
    clienteSelezionato() {
      const id = Number(this.formData.clientId);
      return (this.clients || []).find((c) => c.id === id) || null;
    },
    items() {
      return this.$store.getters["items/list"] ?? [];
    },

    filteredClients() {
      // A campo vuoto i primi clienti per ultimo preventivo [sc-48892]
      const list = this.clients || [];
      let res = clientiPerRicerca(list, this.searchQueryClient);

      const selId = this.formData.clientId;
      const selected = list.find((c) => c.id === selId);
      if (selected && !res.some((c) => c.id === selId)) {
        res = [selected, ...res].slice(0, 10);
      }
      return res;
    },

    // Tutti i nomi da mostrare nella colonna "Articolo"
    itemNames() {
      return (this.formData.items || []).map(
        (row) => this.getItemName(row.itemId, row.category) || ""
      );
    },

    // Lunghezza massima in caratteri tra i nomi
    maxItemLen() {
      return this.itemNames.reduce((m, s) => Math.max(m, s.length), 0);
    },

    // Larghezza dinamica della colonna "Articolo" in ch, con limiti per non rompere il layout
    // - min 180px (leggibile anche con nomi brevi)
    // - valore centrale in ch (caratteri) = maxArtLen + 6 (un po' di respiro)
    // - max 52vw (mai oltre metà viewport nel modale)
    itemColWidth() {
      const ch = Math.max(10, Math.min(this.maxItemLen + 6, 72)); // clamp "logico"
      return `clamp(180px, ${ch}ch, 52vw)`;
    },
    showSelectedItemOrderAlert() {
      return this.isOrderableOutOfStock(this.selectedItem);
    },
    selectedItemOrderLabel() {
      return this.itemSearchLabel(this.selectedItem);
    },

    isStockLocked() {
      return !!this.newItem.itemId;
    },
    clientIdModel: {
      get() {
        const v = this.formData.clientId;
        return v === null || v === undefined ? "" : String(v);
      },
      set(v) {
        // se preferisci tenerlo number nello stato:
        this.formData.clientId = v ? Number(v) : "";
      },
    },
    ddtItems() {
      const AREA_CATS = [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
      ];
      const LINEAR_CATS = ["profili", "sistemi di scarico"];
      return (this.formData.items || []).map((row) => {
        const fullItem =
          this.selectedByKey[`STOCK:${Number(row.itemId)}`] || {};
        const isArea = AREA_CATS.includes((row.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (row.category || "").toLowerCase()
        );
        const qty = isArea || isLinear ? row.boxQuantity ?? 0 : row.pieces ?? 0;
        const uom =
          fullItem.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        return {
          description:
            fullItem.itemName || fullItem.itemCode || `Articolo #${row.itemId}`,
          quantity: qty,
          uom,
        };
      });
    },
  },

  data() {
    return {
      // [sc-49299] spese aggiuntive: null = voce non spuntata
      spese: speseDaPreventivo(),
      dateConfig: {
        locale: Italian,
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "d/m/Y",
        allowInput: true,
        monthSelectorType: "static",
      },
      showBackModal: false,
      pendingNavigation: null,
      navigationConfirmed: false,

      originalSig: "", // firma dello stato iniziale del preventivo, per rilevare modifiche
      // addressInput: "", // testo visibile nell'input
      addressDirty: false, // l'utente ha toccato/modificato l'input

      showDuplicateModal: false,
      lastDuplicateQuoteInfo: { quoteId: null, address: null },
      showDupDocs: false,
      dupDocuments: [],
      dupLoading: false,
      dupError: "",
      dupQuote: null,
      showDdtModal: false,

      showDropdown: false,
      searchQueryItem: "",
      searchQueryClient: "",
      pendingOrderItems: {},
      orderNote: "",
      // selectedItemById: {},
      selectedByKey: {},
      // selectedOutstockById: {},
      panelVisible: true,
      activeSearch: null, // 'client' o 'item' o null
      selectedIndex: -1, // Indice dell'opzione attualmente evidenziata
      response: "",

      formData: {
        location: {
          name: "",
          address: "",
          link: "",
        },
        clientId: "",
        items: [],
        date: "",
        payment_method: "",
        technical_queries: "",
      },
      newItem: {
        itemId: null,
        boxQuantity: 0, // ← scatole, solo per piastrelle
        pieces: 0, // ← pezzi fisici (sanitari) o calcolati
        listPrice: null,
        discountedPrice: null,
        price: "",
        iva: 0, // IVA a 0% di partenza
        category: "",
        // sqmPerBox: "", // solo per piastrelle
      },
      errorMessage: "",
      successMessage: "",
      selectedPlaceId: "",
      paymentOptions: [
        { value: "Contanti", label: "Contanti" },
        { value: "Carta di credito", label: "Carta di credito" },
        { value: "Bonifico", label: "Bonifico" },
        { value: "Assegno", label: "Assegno" }, // [sc-49299]
        { value: "Scegli successivamente", label: "Scegli successivamente" },
      ],
      ivaOptions: [
        { value: 0, label: "0%" },
        { value: 4, label: "4%" },
        { value: 10, label: "10%" },
        { value: 22, label: "22%" },
      ],
      lastUploadedDocName: "",
      // [sc-49299] preventivo come salvato (sconti, acconti, cliente) e dati
      // del form in attesa che si confermi l'anteprima
      quoteSalvato: null,
      payloadInAttesa: null,
    };
  },
  methods: {
    goBack() {
      this.pendingNavigation = null;
      this.showBackModal = true;
    },
    confirmGoBack() {
      this.navigationConfirmed = true;
      this.showBackModal = false;
      if (this.pendingNavigation) {
        this.$router.push(this.pendingNavigation);
      } else {
        // all'elenco, non alla pagina prima nella cronologia: dopo un
        // ricaricamento era di nuovo questa
        this.$router.push({ name: "quotes" });
      }
    },
    // sanitize stringhe nel payload prima dell'update
    sanitizeStr(s, { maxLen = 5000 } = {}) {
      if (s == null) return "";
      let out = String(s).trim();

      // rimuovi caratteri di controllo (tranne tab/newline)
      // out = out.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, " ");

      // strip tag HTML basilare
      out = out.replace(/<[^>]*>/g, " ");

      // normalizza spazi multipli
      out = out.replace(/\s{2,}/g, " ").replace(/\n{3,}/g, "\n\n");

      // limita lunghezza
      if (out.length > maxLen) out = out.slice(0, maxLen);

      return out;
    },
    cleanNumber(n, { min = null } = {}) {
      const v = Number(n);
      if (!Number.isFinite(v)) return 0;
      if (min != null && v < min) return min;
      return v;
    },

    // Riconosce se è piastrella (anche per Fuori Magazzino)
    isTilesObj(src) {
      return (
        src?.category === "piastrelle" ||
        (src?.category === "Fuori Magazzino" &&
          src?.subCategory === "piastrelle")
      );
    },

    // Nome da mostrare ovunque (tabella modale + PDF)
    nameForDisplay(src) {
      return this.itemDisplayFromObj(src || {}).title || "Sconosciuto";
    },

    // Capitallizza la prima lettera
    Cap(s) {
      return (s || "")
        .toString()
        .trim()
        .replace(/^\w/, (c) => c.toUpperCase());
    },

    /**
     * Ritorna { title, meta } a partire dall'oggetto articolo già risolto
     * - Piastrelle (magazzino): titolo = itemName, meta = measureDesc (oppure WxH cm)
     * - Fuori Magazzino/Piastrelle: meta fisso "Piastrelle"
     * - Sanitari/Altro: meta = tipo/posizione (fallback subCategory)
     */
    itemDisplayFromObj(it = {}) {
      // --- GRUPPO AREA (piastrelle, rivestimenti, parquet, laminati, decking) ---
      if (getCategoryGroup(it.category) === "area") {
        const title = it.itemName || "Articolo";
        const meta =
          it.measureDesc ||
          it.formatLabel ||
          (it.widthCm && it.heightCm
            ? `${it.widthCm}×${it.heightCm} cm`
            : null) ||
          it.category;
        return { title, meta };
      }

      // --- FUORI MAGAZZINO: PIASTRELLE ---
      if (
        it.category === "Fuori Magazzino" &&
        it.subCategory === "piastrelle"
      ) {
        const dim =
          it.measureDesc ||
          (it.widthCm && it.heightCm
            ? `${it.widthCm}×${it.heightCm} cm`
            : null);
        const title =
          [it.itemName, dim].filter(Boolean).join(" \u2192 ").trim() ||
          "Articolo fuori magazzino";
        return {
          title,
          meta: "Piastrelle",
        };
      }

      // --- SANITARI / ALTRO (STOCK) ---
      const title =
        it.itemName ||
        [it.brand, it.subBrand].filter(Boolean).join(" \u2192 ").trim() ||
        "Articolo fuori magazzino";

      // accetta anche eventuali chiavi legacy/type/position e usa category come fallback
      const T = this.Cap(it.fixtureType || it.type || it.category || "");
      const P =
        (it.fixturePosition || it.position) === "terra"
          ? "a terra"
          : this.Cap(it.fixturePosition || it.position || "");

      const metaParts = [T, P].filter(Boolean);
      const meta =
        (metaParts.length ? metaParts.join(" ") : null) ||
        this.Cap(it.subCategory || it.category || ""); // <- altro fallback

      return { title, meta };
    },
    /**
     * Ritorna { title, meta } cercando nel negozio partendo da id + hint categoria
     */
    displayForId(id, categoryHint) {
      if (!id) return { title: "Sconosciuto", meta: "" };
      const src = this.getItemFromStore(id, categoryHint) || {};
      if (src && src.id != null) return this.itemDisplayFromObj(src);

      // fallback: prova a leggere la riga già mappata in formData.items
      const row = (this.formData.items || []).find((r) => r.itemId === id);
      if (row) {
        return this.itemDisplayFromObj({
          id,
          category: row.category,
          itemName: row.itemName,
          brand: row.brand,
          subBrand: row.subBrand,
          subCategory: row.subCategory,
        });
      }
      return { title: "Sconosciuto", meta: "" };
    },

    // normalizza solo i campi "di valore" (UI effimera esclusa)
    normalizeForCompare() {
      const f = this.formData || {};
      const L = f.location || {};
      const loc = {
        address: (L.address || "").trim(),
        placeId: L.placeId || L.place_id || "",
      };

      const items = (f.items || [])
        .map((r) => {
          const id = r.itemId;
          const qty = Number(r.boxQuantity || r.pieces || 0);
          const price = Number(r.unitPrice ?? r.price ?? 0);
          const iva = Number(r.iva ?? 22);
          return { cat: r.category || "", id, qty, price, iva };
        })
        .sort(
          (a, b) =>
            a.cat.localeCompare(b.cat) ||
            String(a.id || "").localeCompare(String(b.id || ""))
        );

      return {
        clientId:
          f.clientId === "" || f.clientId === null || f.clientId === undefined
            ? null
            : Number(f.clientId),
        date: f.date || "",
        payment_method: f.payment_method || "",
        technical_queries: f.technical_queries || "",
        location: loc,
        items,
        // [sc-49299] anche una spesa cambiata è una modifica da salvare
        spese: speseDaMandare(this.spese),
      };
    },

    // normalizza la location arrivata dall’API a camelCase + latitude/longitude
    normalizeLocFromApi(raw) {
      if (!raw) {
        return {
          name: "",
          address: "",
          placeId: "",
          latitude: "",
          longitude: "",
          link: "",
        };
      }
      const obj =
        typeof raw === "string"
          ? (() => {
              try {
                return JSON.parse(raw);
              } catch {
                return {};
              }
            })()
          : raw;

      const placeId = obj.placeId || obj.place_id || "";
      // supporta lat/lng legacy o geometry di Google
      const latRaw =
        obj.latitude ?? obj.lat ?? obj?.geometry?.location?.lat ?? "";
      const lngRaw =
        obj.longitude ?? obj.lng ?? obj?.geometry?.location?.lng ?? "";
      let latitude =
        typeof latRaw === "function" ? Number(latRaw()) : Number(latRaw);
      let longitude =
        typeof lngRaw === "function" ? Number(lngRaw()) : Number(lngRaw);

      // fallback: estrae lat/lng dall'URL del link (formato query=lat%2Clng)
      if (
        (!latRaw ||
          !lngRaw ||
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)) &&
        obj.link
      ) {
        const m = obj.link.match(/[?&]query=([-\d.]+)(?:%2C|,)([-\d.]+)/i);
        if (m) {
          latitude = Number(m[1]);
          longitude = Number(m[2]);
        }
      }

      return {
        name: obj.name || "",
        address: obj.address || obj.formatted_address || "",
        placeId,
        latitude: Number.isFinite(latitude) ? latitude : "",
        longitude: Number.isFinite(longitude) ? longitude : "",
        link: obj.link || "",
      };
    },

    computeSignature() {
      // stringify stabile: gli oggetti sono già normalizzati e ordinati
      return JSON.stringify(this.normalizeForCompare());
    },
    captureSnapshot() {
      this.originalSig = this.computeSignature();
    },

    async loadDuplicateQuote() {
      const id = this.lastDuplicateQuoteInfo?.quoteId;
      if (!id) return;

      this.dupLoading = true;
      this.dupError = "";
      this.dupQuote = null;

      try {
        const { data } = await axiosInstance.get(`/quote/${id}`, {
          withCredentials: true,
        });

        // location normalizzata
        let parsedLocation = null;
        try {
          parsedLocation =
            typeof data?.quote?.location === "string"
              ? JSON.parse(data.quote.location)
              : data?.quote?.location || null;
        } catch {
          parsedLocation = null;
        }

        // items normalizzati per il modale
        const items = (data?.quote?.quote_items || [])
          .filter((qi) => !!qi.item)
          .map((qi) => {
            const cat = qi.item.category;
            return {
              category: cat,
              itemName: qi.item.itemName || qi.item.subBrand,
              brand: qi.item.subBrand,
              boxQuantity: cat === "piastrelle" ? qi.quantity : 0,
              pieces: cat === "piastrelle" ? 0 : qi.quantity,
              iva: qi.iva ?? 22,
              price: Number(qi.price ?? 0),
            };
          });

        this.dupQuote = {
          ...data.quote,
          location: parsedLocation,
          totalPrice: data?.quote?.totalPrice ?? null,
          items,
        };
        this.dupDocuments = data?.quote?.documents || [];
      } catch (e) {
        this.dupError =
          e?.response?.data?.message ||
          "Impossibile caricare il preventivo esistente.";
      } finally {
        this.dupLoading = false;
      }
    },

    openDuplicateModal(quoteId, address) {
      this.lastDuplicateQuoteInfo = {
        quoteId: quoteId ?? null,
        address: address ?? null,
      };
      this.dupError = "";
      this.dupQuote = null;
      this.showDuplicateModal = true;
      if (this.lastDuplicateQuoteInfo.quoteId) this.loadDuplicateQuote();
    },
    closeDuplicateModal() {
      this.showDuplicateModal = false;
    },

    number(v) {
      return (typeof v === "number" ? v : Number(v || 0)).toFixed(2);
    },
    money(v) {
      return `€ ${this.number(v)}`;
    },
    formatDocName(name) {
      return (
        (name ?? "")
          .toString()
          // prima togli eventuale ".dev" immediatamente prima di ".pdf"
          .replace(/\.dev(?=\.pdf$)/i, "")
          // poi rimuovi "_N_<numero>.pdf" in fondo
          .replace(/_N_\d+\.pdf$/i, "")
      );
    },
    formatToLocalDate,

    onChangeAddress() {
      this.showDuplicateModal = false;
      this.$nextTick(() =>
        document.getElementById("indirizzo-consegna")?.focus()
      );
    },

    // In Edit carico il preventivo in conflitto e NE COPIO i contenuti nella form attuale,
    // svuotando l'indirizzo: salverai poi con il tuo update
    async onDuplicateWithEdit() {
      const sourceId = this.lastDuplicateQuoteInfo?.quoteId;
      this.showDuplicateModal = false;
      if (!sourceId) return;

      try {
        if (!this.dupQuote || this.dupQuote.id !== sourceId) {
          this.dupLoading = true;
          const { data } = await axiosInstance.get(`/quote/${sourceId}`, {
            withCredentials: true,
          });
          let loc = data?.quote?.location;
          try {
            loc = typeof loc === "string" ? JSON.parse(loc) : loc;
          } catch (error) {
            this.$toast.error(
              "Impossibile caricare il dettaglio di questo preventivo."
            );
          }
          this.dupQuote = { ...data.quote, location: loc };
        }
        const q = this.dupQuote;

        // mappa quote_items -> formData.items (schema Edit)
        const mappedItems = (q.quote_items || [])
          .map((qi) => {
            if (qi.item) {
              const it = qi.item;
              const isTiles = it.category === "piastrelle";
              return {
                itemId: it.id,
                itemName: it.itemName || it.subBrand,
                category: it.category,
                boxQuantity: isTiles ? qi.quantity : 0,
                pieces: isTiles ? 0 : qi.quantity,
                ...prezziRigaSalvata(qi),
                iva: qi.iva ?? 22,
              };
            }
            return null;
          })
          .filter(Boolean);

        // cache per etichette
        (q.quote_items || []).forEach((qi) => {
          // if (qi.item) this.selectedItemById[qi.item.id] = qi.item;
          // if (qi.outstock) this.selectedItemById[qi.outstock.id] = qi.outstock;
          if (qi.item) this.selectedByKey[KEY.STOCK(qi.item.id)] = qi.item;
        });

        // compila la form con i dati del preventivo duplicato, ma svuota l'indirizzo
        this.formData = {
          ...this.formData,
          clientId: q.clientId,
          payment_method: q.payment_method || "",
          technical_queries: q.technical_queries || "",
          items: mappedItems,
          location: {
            name: "",
            address: "",
            link: "",
          },
        };
        // [sc-49299] anche le spese del preventivo di partenza
        this.spese = speseDaPreventivo(q);
        this.newItem = this.resetNewItem();

        this.$nextTick(() =>
          document.getElementById("indirizzo-consegna")?.focus()
        );
        this.$toast.info(
          `Contenuto del preventivo #${q.id} copiato. Inserisci un indirizzo diverso e salva.`
        );
      } catch (e) {
        this.$toast.error("Impossibile duplicare questo preventivo ora.");
      } finally {
        this.dupLoading = false;
      }
    },

    measureDescFrom(w, h) {
      const W = Number(w),
        H = Number(h);
      if (Number.isFinite(W) && Number.isFinite(H) && W > 0 && H > 0) {
        return `${W}×${H} cm`;
      }
      return null;
    },

    rowCategoryGroup(row) {
      const it = this.getItemFromStore(row.itemId, row.category);
      return getCategoryGroup(it?.category);
    },
    isStockTiles(row) {
      return this.rowCategoryGroup(row) === "area";
    },
    qtyCell(row) {
      const g = this.rowCategoryGroup(row);
      if (g === "area" || g === "linear") return Number(row.boxQuantity || 0);
      return Number(row.pieces || row.quantity || 0);
    },
    mqCell(row) {
      const it = this.getItemFromStore(row.itemId, row.category) || {};
      const g = this.rowCategoryGroup(row);
      if (g === "area") {
        const sqmPerBox = Number(it.sqmPerBox) || 0;
        const boxes = Number(row.boxQuantity) || 0;
        const total = boxes * sqmPerBox;
        return total ? `${eur(total)} mq` : "—";
      }
      if (g === "linear") {
        const mlPerBox = Number(it.mlPerBox) || 0;
        const boxes = Number(row.boxQuantity) || 0;
        const total = boxes * mlPerBox;
        return total ? `${eur(total)} ml` : "—";
      }
      return "—";
    },

    categoryHintForRow(row) {
      if (row?.category) return row.category;
      return "STOCK";
    },

    getItemFromStore(id) {
      if (!id) return {};

      const key = KEY.STOCK(id);

      // Store prima: inWarehouse sempre aggiornato rispetto alla cache del preventivo
      const stk =
        (this.items || []).find((i) => i.id === id) ||
        (this.filteredItems || []).find((i) => i.id === id);
      if (stk) {
        this.selectedByKey[key] = stk;
        return stk;
      }

      // Fallback alla cache per articoli non presenti nello store globale
      const cached = this.selectedByKey[key];
      if (cached && String(cached.id) === String(id)) {
        return cached;
      }

      return {};
    },

    // Mappa l'azione updateQuote dallo store
    ...mapActions([
      "fetchQuotes",
      "updateQuote",
      "createOrder",
      "uploadQuoteDocument",
    ]),

    // formatToDateTimeLocal,

    dettaglioVoceArticolo,
    itemSearchLabel(it) {
      if (!it) return "";
      return it?.itemName
        ? `${it.itemName} ${it.measureDesc || ""}`.trim()
        : `${it?.subBrand || ""} ${it?.fixtureType || ""} ${
            it?.fixturePosition || ""
          }`.trim();
    },

    warehouseCount(item) {
      const stock = Number(item?.warehouse?.inWarehouse ?? item?.inWarehouse);
      return Number.isFinite(stock) ? stock : 0;
    },

    isOrderableOutOfStock(item) {
      return !!item?.id && this.warehouseCount(item) <= 0;
    },

    isQtyExceedingStock(row) {
      const item = this.getItemFromStore(
        row.itemId,
        this.categoryHintForRow(row)
      );
      if (!item?.id) return false;
      const stock = this.warehouseCount(item);
      if (stock <= 0) return false;
      return this.qtyCell(row) > stock;
    },

    updateRowQty(row, val) {
      const n = Math.max(0, Math.round(val) || 0);
      const group = this.rowCategoryGroup(row);
      if (group === "area" || group === "linear") {
        row.boxQuantity = n;
        const storeItem = this.getItemFromStore(
          row.itemId,
          this.categoryHintForRow(row)
        );
        row.pieces = n * (storeItem?.piecesPerBox || 1);
      } else {
        row.pieces = n;
      }
    },

    hasIncompleteTileStockData(item) {
      const group = getCategoryGroup(item?.category);
      if (group === "area") return !(Number(item?.sqmPerBox) > 0);
      if (group === "linear") return !(Number(item?.mlPerBox) > 0);
      return false;
    },

    validateQuoteStockItem(rowOrItem) {
      const itemId = rowOrItem?.itemId ?? rowOrItem?.id;
      const item = rowOrItem?.id
        ? rowOrItem
        : this.getItemFromStore(itemId, this.categoryHintForRow(rowOrItem));

      if (!item?.id) return true;
      if (!this.hasIncompleteTileStockData(item)) return true;

      const group = getCategoryGroup(item?.category);
      const field = group === "linear" ? "ml per confezione" : "mq per scatola";
      this.$toast.error(
        `Questo articolo non può essere aggiunto al preventivo: completa prima il campo "${field}" nell'anagrafica articolo.`
      );
      return false;
    },

    validateQuoteItemsBeforeSubmit() {
      return (this.formData.items || []).every((row) =>
        this.validateQuoteStockItem(row)
      );
    },

    isOrderQueued(item) {
      return !!item?.id && !!this.pendingOrderItems[item.id];
    },

    isOrderQueuedRow(row) {
      return !!row?.itemId && !!this.pendingOrderItems[row.itemId];
    },

    orderQuantityForItem(item) {
      if (!item?.id) return 1;

      const stock = this.warehouseCount(item);

      const row = (this.formData.items || []).find(
        (entry) => Number(entry?.itemId) === Number(item.id)
      );
      if (row) {
        const rg = getCategoryGroup(item.category);
        const qty =
          rg === "area" || rg === "linear"
            ? Number(row.boxQuantity || row.quantity || 0)
            : Number(row.pieces || row.quantity || 0);
        if (qty > 0) {
          return stock > 0 ? Math.max(qty - stock, 1) : qty;
        }
      }

      if (this.newItem.itemId === item.id) {
        const g = getCategoryGroup(item.category);
        const qty =
          g === "area" || g === "linear"
            ? Number(this.newItem.boxQuantity || 0)
            : Number(this.newItem.pieces || 0);
        if (qty > 0) return stock > 0 ? Math.max(qty - stock, 1) : qty;
      }
      return 1;
    },

    buildPendingOrderNote(quoteId) {
      return `Ordine per preventivo #${quoteId}`;
    },

    async processPendingOrders(quoteId) {
      const itemIds = Object.keys(this.pendingOrderItems || {}).map(Number);
      if (!quoteId || !itemIds.length) return;

      const results = await Promise.allSettled(
        itemIds.map((itemId) =>
          this.createOrder({
            itemId,
            quantity: this.orderQuantityForItem(this.getItemFromStore(itemId)),
            notes: this.buildPendingOrderNote(quoteId, itemId),
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length) {
        this.$toast.error(
          `${failed.length} ordine/i non creati dopo il salvataggio del preventivo.`
        );
      } else {
        this.$toast.success(
          "Ordini collegati al preventivo creati con successo."
        );
      }
    },

    queueOrderFromQuoteItem(item) {
      if (!item?.id || this.isOrderQueued(item)) return;

      this.pendingOrderItems = {
        ...this.pendingOrderItems,
        [item.id]: true,
      };

      this.$toast.info(
        "Ordine messo in coda. Verrà creato al salvataggio del preventivo."
      );
    },

    unqueueOrderFromQuoteItem(item) {
      if (!item?.id || !this.isOrderQueued(item)) return;

      const next = { ...this.pendingOrderItems };
      delete next[item.id];
      this.pendingOrderItems = next;

      if (this.newItem.itemId === item.id) this.orderNote = "";

      this.$toast.info("Ordine rimosso dalla coda.");
    },

    selectItem(item) {
      this.newItem.itemId = item.id;
      this.orderNote = "";

      this.searchQueryItem = this.itemSearchLabel(item);

      // cache namespaced
      this.selectedByKey[KEY.STOCK(item.id)] = item;

      this.prezziDaArticolo(item);

      this.hideDropdown();
    },

    selectClient(client) {
      this.formData.clientId = client.id;
      this.searchQueryClient = client.clientName;
      this.hideDropdown();
    },

    startSearch(type) {
      this.activeSearch = type; // 'client' o 'item'
      this.showDropdown = true;
      this.selectedIndex = -1;

      // se parte una nuova ricerca resetta l’oggetto che stavi "editando"
      if (type === "item") {
        this.newItem = this.resetNewItem();
      }
      if (type === "client") this.searchQueryClient = "";
      if (type === "item") this.searchQueryItem = "";
    },

    handleKey(event) {
      if (!this.showDropdown) return;

      // Quale array devo navigare?
      let list;
      if (this.activeSearch === "client") {
        list = this.filteredClients;
      } else if (this.activeSearch === "item") {
        list = this.filteredItems;
      } else {
        list = [];
      }

      // ========== freccia giù ==========
      if (event.key === "ArrowDown") {
        this.selectedIndex =
          this.selectedIndex < list.length - 1 ? this.selectedIndex + 1 : 0;
        event.preventDefault();

        // ========== freccia su ==========
      } else if (event.key === "ArrowUp") {
        this.selectedIndex =
          this.selectedIndex > 0 ? this.selectedIndex - 1 : list.length - 1;
        event.preventDefault();

        // ========== ENTER ==========
      } else if (event.key === "Enter") {
        if (this.selectedIndex >= 0 && this.selectedIndex < list.length) {
          const chosen = list[this.selectedIndex];

          if (this.activeSearch === "client") {
            // selezione cliente
            this.formData.clientId = chosen.id;
            this.searchQueryClient = chosen.clientName;
          } else if (this.activeSearch === "item") {
            // selezione articolo di magazzino
            this.newItem.itemId = chosen.id;
            this.searchQueryItem = chosen.itemName
              ? `${chosen.itemName} ${chosen.measureDesc || ""}`.trim()
              : `${chosen.subBrand || ""} ${chosen.fixtureType || ""} ${
                  chosen.fixturePosition || ""
                }`.trim();

            // cache namespaced
            this.selectedByKey[KEY.STOCK(chosen.id)] = chosen;

            // delega tutta la logica a onSelectItem (prezzo, iva, reset, ecc.)
            this.onSelectItem();
          }
          this.showDropdown = false;
          this.selectedIndex = -1;
        }
        event.preventDefault();
      }
    },

    hideDropdown() {
      // Usa un timeout per permettere il click di selezione sul dropdown prima di nasconderlo
      setTimeout(() => {
        this.showDropdown = false;
        this.selectedIndex = -1;
        this.activeSearch = null; // Aggiungi questa linea per resettare l'indice
      }, 300);
    },

    /* Listino e scontato sono imponibili: il prezzo ivato della riga è lo
     scontato (o il listino se manca) più l'IVA scelta. Se l'articolo ha già
     una riga con i prezzi fotografati, si riparte da quelli. */
    prezziDaArticolo(item) {
      const riga = this.formData.items.find(
        (r) => r.itemId === item?.id && r.regolaNuova
      );
      const fonte = riga
        ? { price: riga.listPrice, priceDiscounted: riga.discountedPrice }
        : item;
      this.newItem.listPrice = numeroPrezzo(fonte?.price);
      this.newItem.discountedPrice = numeroPrezzo(fonte?.priceDiscounted);
      this.ricalcolaPrezzoIvato();
    },

    ricalcolaPrezzoIvato() {
      if (!this.newItem.itemId) return;
      const ivato = prezzoIvato(prezzoBase(this.newItem), this.newItem.iva);
      this.newItem.price = ivato;
      this.newItem.unitPrice = ivato;
    },

    // Resta solo per il ramo "Fuori Magazzino" (listino trattato da ivato)
    adjustedPrice(listino, rate) {
      if (rate === 22) return listino;
      const regularPrice = (listino / 1.22).toFixed(2);
      const prezzoIvato = (regularPrice * (1 + rate / 100)).toFixed(2);
      return prezzoIvato;
    },

    /**
     * Gestisce la selezione di un articolo dal dropdown.
     * Chiude il dropdown e aggiorna la searchQuery con il nome dell'articolo selezionato.
     */
    // async onSelectItem() {
    //   this.hideDropdown();

    //   const chosenId = this.newItem.itemId;
    //   const item = this.getItemFromStore(chosenId, "STOCK");
    //   if (!item.id) return;

    //   // reset "pulito", ma con la stessa IVA
    //   const iva = this.newItem.iva || 22;
    //   this.newItem = this.resetNewItem();
    //   this.newItem.itemId = chosenId;
    //   this.newItem.iva = iva;
    //   this.newItem.category = item.category || "";

    //   // disaccoppia l’altra selezione
    //   this.formData.outstockId = null;
    //   this.searchQueryOutstock = "";

    //   this.selectedItemById[item.id] = item;
    //   this.searchQueryItem = item.itemName
    //     ? `${item.itemName} ${item.measureDesc || ""}`.trim()
    //     : `${item.subBrand || ""} ${item.fixtureType || ""} ${
    //         item.fixturePosition || ""
    //       }`.trim();

    //   const prezzoIvato = this.adjustedPrice(item.price, this.newItem.iva);
    //   this.newItem.price = prezzoIvato;
    //   this.newItem.unitPrice = prezzoIvato;
    // },

    async onSelectItem() {
      this.hideDropdown();
      const chosenId = this.newItem.itemId;
      const item = this.getItemFromStore(chosenId, "STOCK");
      if (!item.id) return;

      const iva = this.newItem.iva ?? 0;
      this.newItem = this.resetNewItem();
      this.newItem.itemId = chosenId;
      this.newItem.iva = iva;
      this.newItem.category = item.category || "";

      this.selectedByKey[KEY.STOCK(item.id)] = item;
      this.searchQueryItem = item.itemName
        ? `${item.itemName} ${item.measureDesc || ""}`.trim()
        : `${item.subBrand || ""} ${item.fixtureType || ""} ${
            item.fixturePosition || ""
          }`.trim();

      this.prezziDaArticolo(item);
    },

    handleClientSelection() {
      this.showDropdown = false;
      this.searchQueryClient = this.getClientName(this.formData.clientId);
    },
    /**
     * Recupera i dati del preventivo da modificare.
     * Viene chiamato appena il componente è montato.
     */
    async fetchQuoteData() {
      try {
        this.errorMessage = "";
        const response = await axiosInstance.get(`/quote/${this.quoteId}`);
        // Adatta il percorso a seconda della tua API
        if (response.status === 200) {
          const quoteData = response.data.quote; // notare che i dati stanno sotto `quote`
          this.quoteSalvato = quoteData;
          // console.log("fetchQuoteData quoteData", quoteData);
          // console.log("quoteData.response.data.quote", quoteData);
          // items viene dal server con item + item.QuoteItem
          // Creiamo un array "flat" con le proprietà necessarie
          // console.log("quoteData.quote_items", quoteData.quote_items);
          const mappedItems = (quoteData.quote_items || [])
            .map((qi) => {
              if (qi.item) {
                // ───── articolo di magazzino ─────
                const it = qi.item;
                const isTiles = it.category === "piastrelle";

                return {
                  itemId: it.id,
                  itemName: it.itemName || it.subBrand,
                  category: it.category,
                  boxQuantity: isTiles ? qi.quantity : 0,
                  pieces: isTiles ? 0 : qi.quantity,
                  ...prezziRigaSalvata(qi),
                  iva: qi.iva,
                };
              } else {
                return null;
              }
            })
            .filter(Boolean);

          // ⚠️ subito dopo const mappedItems = ...
          (quoteData.items || []).forEach((it) => {
            // sono articoli di MAGAZZINO
            if (it && it.id != null) {
              this.selectedByKey[KEY.STOCK(it.id)] = it;
            }
          });

          (quoteData.quote_items || []).forEach((qi) => {
            if (qi.item && qi.item.id != null) {
              // stock
              this.selectedByKey[KEY.STOCK(qi.item.id)] = {
                ...qi.item,
                // in certi dump itemName è "", ma brand/subBrand ci sono
                // non forziamo qui: ci pensa nameForDisplay al fallback
              };
            }
          });

          this.formData.items = mappedItems;
          // console.log("this.formData.items", this.formData.items);
          this.formData.date = toDateLocal(quoteData.date);

          // this.formData.date = this.formatToDateTimeLocal(quoteData.date);
          this.formData.clientId = quoteData.clientId || "";
          const c = (this.clients || []).find(
            (x) => x.id === this.formData.clientId
          );
          this.searchQueryClient = c
            ? this.getClientLabel(c)
            : quoteData.clientName || "";
          // this.searchQueryClient = quoteData.clientName || "";
          // this.formData.operatorId = quoteData.operatorId || "";

          // this.formData.eventName = quoteData.eventName || ""; // Se non esiste, assegna una stringa vuota

          // Se location non esiste, assegna un oggetto vuoto
          // this.formData.location = asLoc(quoteData.location) || {
          //   name: "",
          //   address: "",
          //   placeId: "",
          //   latitude: "",
          //   longitude: "",
          // };

          // this.addressInput = this.formData.location.address || "";
          // this.lastValidLocation = this.isAddressValid
          //   ? { ...this.formData.location }
          //   : null;

          const normLoc = this.normalizeLocFromApi(quoteData.location);
          this.formData.location = normLoc;
          // this.addressInput = normLoc.address || "";
          // this.lastValidLocation =
          //   normLoc.placeId && normLoc.latitude && normLoc.longitude
          //     ? { ...normLoc }
          //     : null;

          // this.addressDirty = false;
          // this.locationSelected = !!this.lastValidLocation;

          this.addressDirty = false;

          this.formData.payment_method = quoteData.payment_method;
          this.formData.technical_queries = quoteData.technical_queries || ""; // Se non esiste, assegna una stringa vuota
          this.spese = speseDaPreventivo(quoteData); // [sc-49299]

          const existingDocs = quoteData.documents || [];
          if (existingDocs.length > 0) {
            this.lastUploadedDocName = existingDocs.at(-1).name;
          }

          this.$nextTick(() => {
            this.successMessage = `Dati del preventivo con ID:${this.quoteId} caricati con successo`;
            this.$toast.success(this.successMessage);
            this.captureSnapshot(); // stato iniziale caricato
          });
        }
      } catch (error) {
        // console.error("fetchQuoteData error:", error);
        this.errorMessage =
          error.response?.data?.message ||
          "Impossibile caricare i dati del preventivo.";
        this.$toast.error(this.errorMessage);
      }
    },

    toggleItemsList() {
      this.panelVisible = !this.panelVisible;
    },

    addOrUpdateItem() {
      this.errorMessage = "";
      this.successMessage = "";

      const isAreaOrLinear =
        this.selectedItemGroup === "area" ||
        this.selectedItemGroup === "linear";

      const qty = isAreaOrLinear
        ? Number(this.newItem.boxQuantity || 0)
        : Number(this.newItem.pieces || 0);

      if (!this.newItem.itemId || qty <= 0 || !this.newItem.unitPrice) {
        this.$nextTick(() => {
          this.errorMessage =
            "Compila tutti i campi prima di aggiungere un articolo.";
          this.$toast.error(this.errorMessage);
        });
        return;
      }

      if (this.erroreScontatoRiga) {
        this.errorMessage = this.erroreScontatoRiga;
        this.$toast.error(this.errorMessage);
        return;
      }

      if (!this.validateQuoteStockItem(this.selectedItem)) return;

      const full = this.getItemFromStore(
        this.newItem.itemId,
        this.categoryHintForRow(this.newItem)
      );
      if (full.id) this.selectedByKey[KEY.STOCK(full.id)] = full;

      const existing = this.formData.items.find(
        (r) => r.itemId === this.newItem.itemId
      );

      if (existing) {
        if (isAreaOrLinear) {
          existing.boxQuantity = this.newItem.boxQuantity;
          existing.pieces = 0;
        } else {
          existing.pieces = this.newItem.pieces;
        }
        existing.unitPrice = this.newItem.unitPrice;
        existing.price = this.newItem.unitPrice;
        existing.listPrice = this.newItem.listPrice;
        existing.discountedPrice = numeroPrezzo(this.newItem.discountedPrice);
        existing.regolaNuova = true;
        existing.quantity = existing.pieces || existing.boxQuantity;
        existing.iva = this.newItem.iva;

        this.$nextTick(() => {
          this.successMessage = "Quantità aggiornata per l'articolo esistente.";
          this.$toast.success(this.successMessage);
        });
        return;
      }

      // nuova riga
      this.formData.items.push({
        itemId: this.newItem.itemId,
        category: this.newItem.category || this.selectedItem.category,
        boxQuantity: isAreaOrLinear ? this.newItem.boxQuantity : 0,
        pieces: isAreaOrLinear ? 0 : this.newItem.pieces,
        quantity: isAreaOrLinear
          ? this.newItem.boxQuantity
          : this.newItem.pieces,
        unitPrice: this.newItem.unitPrice, // prezzo unitario
        price: this.newItem.unitPrice,
        listPrice: this.newItem.listPrice,
        discountedPrice: numeroPrezzo(this.newItem.discountedPrice),
        regolaNuova: true,
        iva: this.newItem.iva,
      });

      this.$nextTick(() => {
        this.successMessage = "Articolo aggiunto con successo.";
        this.$toast.success(this.successMessage);
      });

      this.newItem = this.resetNewItem();
      this.searchQueryItem = "";
      this.showDropdown = false;
      // this.newItem.itemId = null;
      // this.formData.outstockId = null;
    },

    removeItem(index) {
      this.formData.items.splice(index, 1);
    },

    saveToLocalStorage(key, data) {
      localStorage.setItem(key, JSON.stringify(data));
    },

    loadFromLocalStorage(key) {
      const savedData = localStorage.getItem(key);
      if (savedData) {
        return JSON.parse(savedData);
      }
      return null;
    },

    getItemName(id, category) {
      if (!id) return "Sconosciuto";
      const src = this.getItemFromStore(id, category);
      if (src && src.id != null) return this.nameForDisplay(src);

      // 🔚 ultima spiaggia: usa la riga già mappata in formData.items
      const row = (this.formData.items || []).find((r) => r.itemId === id);
      if (row) {
        // per sanitari/altro la mappatura include almeno itemName (o subBrand)
        return row.itemName || "Sconosciuto";
      }
      return "Sconosciuto";
    },

    getClientName(clientId) {
      const client = this.clients.find((c) => c.id === clientId);
      return client ? client.clientName : "Sconosciuto";
    },

    getClientLabel(c) {
      if (!c) return "";
      if (c.email) return `${c.clientName} - (${c.email})`;
      if (c.pec) return `${c.clientName} - (${c.pec})`;
      return c.clientName;
    },

    eur,
    importoAnteprima(v) {
      return v == null ? "—" : `€ ${eur(v)}`;
    },
    formatIva(iva) {
      const percentageSymbol = "%";
      return `${iva} ${percentageSymbol}`;
    },

    // Formato italiano, come l'anteprima della riga: € 1.234,50
    formatPrice(price) {
      return `€ ${eur(price)}`;
    },

    unitForRow(row) {
      const it = this.getItemFromStore(row.itemId, row.category) || {};
      if (
        String(row?.itemName || it?.itemName || "")
          .toLowerCase()
          .includes("battiscopa")
      )
        return "€/mL";
      const g = getCategoryGroup(it.category);
      if (g === "area") return "€/mq";
      if (g === "linear") return "€/ml";
      return "€/pz";
    },

    // mqForRow(row) {
    //   // console.log("row", row);
    //   const item = this.getItemFromStore(
    //     row.itemId,
    //     row.category
    //   );
    //   // console.log("mqForRow item", item);
    //   // solo piastrelle
    //   if (item.category !== "piastrelle" && item.subCategory === "piastrelle") {
    //     return row.pieces;
    //   }
    //   const sqmPerBox = Number(item.sqmPerBox) || 0;
    //   const boxes = Number(row.boxQuantity) || 0;
    //   const total = sqmPerBox * boxes;
    //   return total ? total.toFixed(2) : "";
    // },

    mqForRow(row) {
      const it = this.getItemFromStore(row.itemId, row.category) || {};

      // Stock piastrelle: scatole × mqPerBox
      if (it.category === "piastrelle") {
        const sqmPerBox = Number(it.sqmPerBox) || 0;
        const boxes = Number(row.boxQuantity) || 0;
        const total = sqmPerBox * boxes;
        return total ? total.toFixed(2) : "N/D";
      }

      // Fuori magazzino piastrelle: i mq coincidono con 'pieces'
      if (
        it.category === "Fuori Magazzino" &&
        it.subCategory === "piastrelle"
      ) {
        const sqm = Number(row.pieces) || 0;
        return sqm ? sqm.toFixed(2) : "N/D";
      }

      // Altri casi
      return "N/D";
    },

    resetNewItem() {
      return {
        itemId: null,
        boxQuantity: 0,
        pieces: 0,
        listPrice: null,
        discountedPrice: null,
        price: "",
        category: "",
        quantity: 0, // quantità pezzi per articoli fuori magazzino
        iva: 0,
      };
    },

    // Righe della tabella articoli del PDF (colonne e formati in pdfArticoli)
    buildQuoteTable(quote) {
      // quote = updatedQuote (ha quote_items con .item)
      const righe = (quote.quote_items || []).map((qi) => {
        const src = qi.item || {};
        const isTiles = src.category === "piastrelle";

        let qty;
        if (isTiles) {
          const sqmPerBox = Number(src.sqmPerBox || 0);
          qty = sqmPerBox
            ? Number((sqmPerBox * qi.quantity).toFixed(2))
            : Number(qi.quantity || 0);
        } else {
          qty = Number(qi.quantity || 0);
        }

        const desc = isTiles
          ? [src.itemName, src.measureDesc].filter(Boolean).join(" ") || "-"
          : src.itemName ||
            [src.brand, src.subBrand].filter(Boolean).join(" ") ||
            "-";

        return {
          group: (src.category || "generale").toUpperCase(),
          code: src.itemCode ?? src.code ?? "-",
          desc,
          unit: String(desc).toLowerCase().includes("battiscopa")
            ? "mL"
            : isTiles
            ? "mQ"
            : "pz",
          qty,
          listPrice: qi.listPrice ?? null,
          discountedPrice: qi.discountedPrice ?? null,
          unitPrice: Number(qi.price || 0),
          iva: Number(qi.iva ?? 22),
        };
      });

      return righe;
    },

    // [sc-49299] PDF caricato dalla finestra di stampa: è l'ultimo documento
    dopoStampa(nome) {
      if (nome) this.lastUploadedDocName = nome;
    },

    // Dati da mandare all'API dopo i controlli del form; null se manca qualcosa
    preparaAggiornamento() {
      // 0) Validazione base
      if (this.formData.items.length === 0) {
        this.$nextTick(() => {
          this.errorMessage = "Aggiungi almeno un articolo prima di salvare";
          this.$toast.error(this.errorMessage);
        });
        return null;
      }

      // [sc-49299] spesa spuntata senza importo valido
      const erroreSpese = primoErroreSpese(this.spese);
      if (erroreSpese) {
        this.$toast.error(erroreSpese);
        return null;
      }

      // 1) pulizia item "in input" se presente
      if (
        this.newItem.itemId &&
        (this.newItem.boxQuantity ||
          this.newItem.pieces ||
          this.newItem.quantity)
      ) {
        if (!this.validateQuoteStockItem(this.selectedItem)) return null;
        this.formData.items.push({ ...this.newItem, regolaNuova: true });
      }

      if (!this.validateQuoteItemsBeforeSubmit()) return null;

      // 2) costruisci items per API con coercizioni sicure
      const apiItems = this.formData.items.map((row) => {
        const qty = this.cleanNumber(row.boxQuantity || row.pieces, {
          min: 0,
        });
        return {
          itemId: row.itemId,
          quantity: qty,
          price: this.cleanNumber(row.unitPrice ?? row.price, { min: 0 }), // prezzo unitario ivato
          iva: this.cleanNumber(row.iva ?? 22, { min: 0 }),
          category: row.category || "",
          // Regola nuova (listino e scontato fotografati, ivato calcolato
          // dall'API) solo per le righe nuove, toccate o già fotografate:
          // una riga vecchia non toccata resta identica.
          ...(row.regolaNuova
            ? { discountedPrice: numeroPrezzo(row.discountedPrice) }
            : {}),
        };
      });

      // 3) payload base
      let payload = {
        ...this.formData,
        items: apiItems,
        // [sc-49299] tutte e tre: null toglie la spesa
        ...speseDaMandare(this.spese),
      };

      // 4) normalizza data: type="date" dà YYYY-MM-DD, il backend vuole ISO con ora locale
      if (payload.date && payload.date.length === 10) {
        payload.date = new Date(payload.date + "T00:00:00").toISOString();
      }

      // 5) sanitize campi testuali
      payload.payment_method = (payload.payment_method || "").trim();
      payload.technical_queries = this.sanitizeStr(payload.technical_queries, {
        maxLen: 8000,
      });

      // 5) location opzionale + normalizzazione
      if (payload.location) {
        payload.location = this.normalizeLocFromApi(payload.location);
        if (!indirizzoValido(payload.location.address)) {
          delete payload.location; // senza testo non c'è niente da aggiornare
        }
      }

      // 6) client coerente
      payload.clientId =
        payload.clientId === "" || payload.clientId == null
          ? null
          : Number(payload.clientId);

      // 7) rimuovi duplicati nel payload (difesa ulteriore)
      const keyOf = (r) => `${r.itemId ?? ""}`;
      const seen = new Set();
      payload.items = payload.items.filter((r) => {
        const k = keyOf(r);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      return payload;
    },

    // [sc-49299] «Anteprima preventivo» non salva: apre l'anteprima con i
    // dati del form. Si salva solo con «Aggiorna e scarica PDF».
    onUpdateQuoteAndGeneratePdf() {
      this.errorMessage = "";
      this.successMessage = "";
      const payload = this.preparaAggiornamento();
      if (!payload) return;
      this.payloadInAttesa = payload;
      this.apriStampa(this.datiAnteprima(payload));
    },

    // Dati del PDF dal form, nella forma del preventivo che l'API restituisce
    // dopo l'aggiornamento: stessi prezzi ivati, stesso totale, sconto e
    // maggiorazione in percentuale ricalcolati come fa l'API
    datiAnteprima(payload) {
      const salvato = this.quoteSalvato || {};
      const righeSalvate = new Map(
        (salvato.quote_items || []).map((qi) => [qi.item?.id, qi])
      );
      const quote_items = payload.items.map((r) => {
        const item = this.getItemFromStore(r.itemId, "STOCK") || {};
        const vecchia = righeSalvate.get(r.itemId);
        let listPrice = vecchia?.listPrice ?? null;
        let discountedPrice = vecchia?.discountedPrice ?? null;
        let price = r.price;
        if ("discountedPrice" in r) {
          listPrice = numeroPrezzo(vecchia?.listPrice ?? item.price);
          discountedPrice = r.discountedPrice;
          price = prezzoIvatoRiga({ listPrice, discountedPrice, iva: r.iva });
        }
        return {
          item,
          quantity: r.quantity,
          iva: r.iva,
          listPrice,
          discountedPrice,
          price,
        };
      });
      const totale = totaleArticoli(
        quote_items.map((qi) => ({
          price: qi.price,
          quantity: qi.quantity,
          category: qi.item.category,
          sqmPerBox: qi.item.sqmPerBox,
          mlPerBox: qi.item.mlPerBox,
        }))
      );
      const cliente =
        (this.clients || []).find((c) => c.id === payload.clientId) ||
        salvato.Client ||
        {};
      const quote = {
        ...salvato,
        ...payload,
        id: salvato.id ?? Number(this.quoteId),
        location: payload.location || salvato.location,
        ...importiDaPercentuali(totale, salvato),
        quote_items,
      };
      return datiPdf(quote, {
        cliente: clientePerPdf(cliente),
        righe: this.buildQuoteTable(quote),
        totaleArticoli: totale,
      });
    },

    // [sc-49299] anteprima confermata: aggiorna, poi i dati del PDF dal
    // preventivo aggiornato. null se l'aggiornamento non è andato.
    async salvaDaStampa() {
      const payload = this.payloadInAttesa;
      try {
        // => Update preventivo
        const response = await this.updateQuote({
          quoteId: this.quoteId,
          payload,
        });
        this.response = response;

        if (response?.status !== 200 && response?.status !== 201) {
          // ⬇️ ramo 409
          if (
            response?.status === 409 &&
            response?.data.code === "QUOTE_DUPLICATE"
          ) {
            this.segnalaDuplicato(response?.data);
            return null;
          }

          // ...altri errori
          this.errorMessage =
            response.data?.details ||
            response.data?.message ||
            "Impossibile aggiornare il preventivo.";
          this.$toast.error(this.errorMessage);
          this.successMessage = "";
          return null;
        }

        // Aggiorna stato locale post-update
        const { updatedQuote, totalPrice } = response.data.quote;
        this.payloadInAttesa = null;
        this.quoteSalvato = updatedQuote;
        await this.processPendingOrders(
          Number(updatedQuote?.id || this.quoteId)
        );
        this.formData.totalPrice = Number(totalPrice);

        // Rimappa gli items per la tabella
        this.formData.items = (updatedQuote.quote_items || [])
          .map((qi) => {
            if (!qi.item) return null;
            const it = qi.item;
            const isTiles = it.category === "piastrelle";
            return {
              itemId: it.id,
              itemName: it.itemName || it.subBrand,
              category: it.category,
              boxQuantity: isTiles ? qi.quantity : 0,
              pieces: isTiles ? 0 : qi.quantity,
              sqmPerBox: isTiles ? Number(it.sqmPerBox) || 0 : 0,
              ...prezziRigaSalvata(qi),
              iva: qi.iva,
            };
          })
          .filter(Boolean);

        await this.$nextTick();
        this.captureSnapshot(); // stato aggiornato salvato

        this.$nextTick(() => {
          this.successMessage =
            response.data.message || "Preventivo aggiornato con successo!";
          this.$toast.success(this.successMessage);
        });

        this.newItem = this.resetNewItem();
        this.orderNote = "";
        this.pendingOrderItems = {};

        return datiPdf(updatedQuote, {
          cliente: clientePerPdf(updatedQuote.Client),
          righe: this.buildQuoteTable(updatedQuote),
          totaleArticoli: parseFloat(totalPrice || 0),
        });
      } catch (error) {
        const resp = error?.response;

        // ⬇️ ramo 409
        if (resp?.status === 409 && resp?.data?.code === "QUOTE_DUPLICATE") {
          this.segnalaDuplicato(resp?.data);
          return null;
        }

        // ...altri errori
        this.errorMessage =
          resp?.data?.message ||
          resp?.data?.details ||
          resp?.data?.error ||
          "Errore durante l'aggiornamento del preventivo.";
        this.$toast.error(this.errorMessage);
        this.successMessage = "";
        return null;
      }
    },

    segnalaDuplicato(d = {}) {
      const details = (typeof d.details === "object" && d.details) || {};
      const id = details.quoteId ?? d.quoteId ?? null;
      const address =
        details.address ?? this.formData?.location?.address ?? null;
      const msg =
        d.message ||
        d.details ||
        "Esiste già un preventivo per questo cliente allo stesso indirizzo in corso...";

      this.$toast.error(msg);
      this.openDuplicateModal(id, address);
    },

    onAddressTyping() {
      this.addressDirty = true;
      const L = this.formData.location || {};
      // riscrivere l'indirizzo scarta i dati Maps del vecchio preventivo:
      // indicavano un altro posto. Il link torna a essere una ricerca testuale.
      this.formData.location = {
        name: "",
        address: L.address || "",
        link: linkMaps(L.address),
      };
    },

    resetLocation() {
      this.formData.location = { name: "", address: "", link: "" };
      this.addressDirty = false;
    },

    // --- pulizia selezione articolo di magazzino ---
    clearItemSelection() {
      const iva = this.newItem.iva ?? 0; // preserva l'IVA scelta
      Object.assign(this.newItem, this.resetNewItem(), { iva });
      this.searchQueryItem = "";
      this.orderNote = "";
    },
  },

  mounted() {
    this.panelVisible = window.innerWidth >= 768;
    this.$nextTick(() => {});

    this.fetchQuoteData();
  },

  watch: {
    clients(newList) {
      if (this.formData.clientId && !this.searchQueryClient) {
        const c = newList.find((x) => x.id === this.formData.clientId);
        if (c) this.searchQueryClient = this.getClientLabel(c);
      }
    },

    showDuplicateModal(v) {
      if (v && this.lastDuplicateQuoteInfo?.quoteId) this.loadDuplicateQuote();
    },
    lastDuplicateQuoteInfo: {
      deep: true,
      handler(nv) {
        if (this.showDuplicateModal && nv?.quoteId) this.loadDuplicateQuote();
      },
    },

    // 🔎 Articoli di magazzino
    searchQueryItem(newQ, oldQ) {
      // se l'utente cancella il testo ed è presente una selezione → pulisci e riabilita l'altro input
      if (oldQ && !newQ.trim() && this.newItem.itemId)
        this.clearItemSelection();

      // niente testo: tornano i primi articoli, come al click sul campo
      this.debouncedFetchItems(newQ.trim());
    },

    // 🔒 mutua esclusione (come già facevi)
    "newItem.itemId"(v) {
      if (v) {
        // item selected
      }
    },

    // "formData.outstockId"(v) {
    //   if (!v) return;
    //   this.newItem.outstockId = v;
    //   this.newItem.itemId = null;
    // },

    // 💶 ricalcolo prezzo al cambio IVA (invariato salvo minimi ritocchi)
    // "newItem.iva"(val) {
    //   if (this.newItem.itemId) {
    //     const base = this.getItemFromStore(this.newItem.itemId).price;
    //     this.newItem.price = this.adjustedPrice(base, val);
    //     this.newItem.unitPrice = this.newItem.price;
    //     return;
    //   }
    //   if (this.newItem.outstockId) {
    //     const outstock = this.getItemFromStore(
    //       this.newItem.outstockId,
    //       "Fuori Magazzino"
    //     );
    //     const base =
    //       outstock.subCategory === "piastrelle"
    //         ? outstock.sqmPrice
    //         : outstock.price;
    //     this.newItem.unitPrice = Number(this.adjustedPrice(base, val));
    //     const qty = this.newItem.pieces || 0;
    //     this.newItem.price = (this.newItem.unitPrice * qty).toFixed(2);
    //   }
    // },

    // IVA o scontato cambiati: il prezzo ivato riparte sempre dalla base
    "newItem.iva"() {
      this.ricalcolaPrezzoIvato();
    },
    "newItem.discountedPrice"() {
      this.ricalcolaPrezzoIvato();
    },

    // 📝 sync riga in tabella
    newItem: {
      handler(nv) {
        if (!nv.itemId) return;
        const row = this.formData.items.find((x) => x.itemId === nv.itemId);
        if (!row) return;
        const group = getCategoryGroup(this.selectedItem?.category);
        const isBoxBased = group === "area" || group === "linear";
        const qty = isBoxBased ? nv.boxQuantity : nv.pieces;
        row.quantity = qty;
        row.boxQuantity = isBoxBased ? nv.boxQuantity || 0 : 0;
        row.pieces = isBoxBased ? 0 : nv.pieces || 0;
        row.price = nv.price;
        row.unitPrice = nv.price;
        row.listPrice = nv.listPrice;
        row.discountedPrice = numeroPrezzo(nv.discountedPrice);
        row.regolaNuova = true; // la riga è stata toccata
        row.iva = nv.iva;
      },
      deep: true,
    },
  },
};
</script>

<style src="@/css/CreateQuotePage.css"></style>
